/**
 * Offer-line scope: only what lives on the Kalkyle overview.
 *
 * Firm templates (offer_id nil) and orphan nested_calculations that are not
 * attached as overview lines must not appear under Kundevisning.
 */

import { itemNodeKey } from "./presentation-rules-shape";

export function isFirmCalculationTemplate(calc) {
  if (!calc || typeof calc !== "object") return false;
  if (calc.is_template === true) return true;
  if (Object.prototype.hasOwnProperty.call(calc, "offer_id")) {
    return calc.offer_id == null;
  }
  return false;
}

/** One offer page: first root calc (parent_calculation_id null). */
export function pageRootCalculations(list) {
  const roots = (Array.isArray(list) ? list : []).filter(
    (calc) => calc?.parent_calculation_id == null && !isFirmCalculationTemplate(calc)
  );
  return roots.length ? [roots[0]] : [];
}

function nestedFromItem(item) {
  if (!item || item._destroy) return null;
  if (item.nested_calculation && !isFirmCalculationTemplate(item.nested_calculation)) {
    return item.nested_calculation;
  }
  return null;
}

/**
 * Calc ids that appear on the overview (root + item-linked packages).
 * Does not walk association-only nested_calculations (orphan maler).
 */
export function collectOverviewCalculationIds(offer) {
  const ids = new Set();
  const walk = (calc) => {
    if (!calc?.id || isFirmCalculationTemplate(calc)) return;
    const id = Number(calc.id);
    if (!Number.isFinite(id) || ids.has(id)) return;
    ids.add(id);
    (calc.items || []).forEach((item) => {
      const nested = nestedFromItem(item);
      if (nested) walk(nested);
    });
  };
  pageRootCalculations(offer?.calculations).forEach(walk);
  return ids;
}

export function collectOverviewLines(offer) {
  const lines = [];
  const walk = (calc, depth) => {
    if (!calc || isFirmCalculationTemplate(calc)) return;
    (calc.items || []).forEach((item) => {
      if (!item || item._destroy) return;
      lines.push({
        item,
        calc,
        depth,
        nodeKey: itemNodeKey(item) || (item.draggableId ? `item:${item.draggableId}` : null),
      });
      const nested = nestedFromItem(item);
      if (nested) walk(nested, depth + 1);
    });
  };
  pageRootCalculations(offer?.calculations).forEach((calc) => walk(calc, 0));
  return lines;
}

/**
 * Drop presentation rows whose calculation_id is not on the overview.
 * Rows without calculation_id (Offer::Group chapters) are kept.
 */
export function filterItemsToOfferOverview(items, offer) {
  const ids = collectOverviewCalculationIds(offer);
  if (!ids.size) {
    return (items || []).filter((item) => {
      if (item?.is_template) return false;
      if (item?.offer_id === null && item?.calculation_id != null) return false;
      return true;
    });
  }
  return (items || []).filter((item) => {
    if (!item || item.delete) return false;
    const cid = item.calculation_id;
    if (cid == null || cid === "") return true;
    return ids.has(Number(cid));
  });
}
